/**
 * Consolidated Theme Manager
 * Handles all theme-related functionality including light/dark mode, color themes, density, and contrast
 */

import { setTheme } from '@dialpad/dialtone/themes/config';
import DpLight from '@dialpad/dialtone/themes/dp-light';
import DpDark from '@dialpad/dialtone/themes/dp-dark';
import { simpleDebugLog } from './simpleDebugLogger';

// Re-use a single MediaQueryList instance so add/removeEventListener reference the same object
const prefersDarkQuery = window.matchMedia('(prefers-color-scheme: dark)');

// ==================== TYPE DEFINITIONS ====================
export type ThemeType = 'light' | 'dark' | 'system';
export type ColorThemeName = 'default' | 'melon' | 'aubergine' | 'sunflower' | 'dune';
export type DensityName = 'compact' | 'medium' | 'comfortable';
export type ContrastMode = 'normal' | 'high';
export interface ColorTheme {
  name: ColorThemeName;
  displayName: string;
  previewColor: string; // CSS color for preview circles
}

export interface DensityOption {
  name: DensityName;
  displayName: string;
}

export interface ThemeVariant {
  [variable: string]: string;
}

export interface ThemeDefinition {
  light: ThemeVariant;
  dark: ThemeVariant;
  contrast: ThemeVariant;
}

// ==================== CONFIGURATION ====================
// Default values
const DEFAULT_THEME: ThemeType = 'system';
const DEFAULT_COLOR_THEME: ColorThemeName = 'default';
const DEFAULT_DENSITY: DensityName = 'medium';
const DEFAULT_CONTRAST: ContrastMode = 'normal';

// Available color themes
const COLOR_THEMES: ColorTheme[] = [
  {
    name: 'default',
    displayName: 'Default',
    previewColor: 'var(--dt-color-purple-300)',
  },
  {
    name: 'melon',
    displayName: 'Melon',
    previewColor: 'var(--dt-color-berry-300)',
  },
  {
    name: 'aubergine',
    displayName: 'Aubergine',
    previewColor: 'var(--dt-color-teal-400)',
  },
  {
    name: 'sunflower',
    displayName: 'Sunflower',
    previewColor: 'var(--dt-color-gold-400)',
  },
  {
    name: 'dune',
    displayName: 'Dune',
    previewColor: 'var(--dt-color-tan-400)',
  },
];

// Available density options
const DENSITY_OPTIONS: DensityOption[] = [
  { name: 'compact', displayName: 'Compact' },
  { name: 'medium', displayName: 'Medium' },
  { name: 'comfortable', displayName: 'Comfortable' },
];

// ==================== STATE MANAGEMENT ====================
let currentTheme: ThemeType = DEFAULT_THEME;
let systemThemeListener: ((event: MediaQueryListEvent) => void) | null = null;
let contrastStylesLoaded = false;

// ==================== UTILITY FUNCTIONS ====================
const isValidTheme = (theme: string): theme is ThemeType => {
  return ['light', 'dark', 'system'].includes(theme);
};

const isValidColorTheme = (theme: string): theme is ColorThemeName => {
  return COLOR_THEMES.some(t => t.name === theme);
};

const isValidDensity = (density: string): density is DensityName => {
  return DENSITY_OPTIONS.some(d => d.name === density);
};

const isValidContrast = (contrast: string): contrast is ContrastMode => {
  return ['normal', 'high'].includes(contrast);
};

/**
 * Dynamically load contrast styles
 */
const loadContrastStyles = async (): Promise<void> => {
  if (contrastStylesLoaded) return;

  try {
    await import('../styles/contrast.less');
    contrastStylesLoaded = true;
  } catch (error) {
    simpleDebugLog.warn('Failed to load contrast styles:', error);
  }
};

/**
 * Note: Unloading CSS is complex in Vite/bundlers, so we'll keep styles loaded
 * once imported but only apply them conditionally via data attributes
 */

// ==================== THEME APPLICATION FUNCTIONS ====================

/**
 * Apply base theme (light/dark/system) using Dialtone's theme system
 */
export const applyTheme = (theme: ThemeType): void => {
  // Clear any color theme related localStorage items when changing base theme
  localStorage.removeItem('colorTheme');

  // Remove previous system theme listener if any
  if (systemThemeListener) {
    prefersDarkQuery.removeEventListener('change', systemThemeListener);
    systemThemeListener = null;
  }

  // Store the theme preference
  currentTheme = theme;
  localStorage.setItem('theme', theme);

  if (theme === 'system') {
    // Apply based on system preference using Dialtone themes
    const prefersDark = prefersDarkQuery.matches;
    setTheme(prefersDark ? DpDark : DpLight);

    // Listen for system preference changes
    systemThemeListener = (e: MediaQueryListEvent) => {
      setTheme(e.matches ? DpDark : DpLight);
    };
    prefersDarkQuery.addEventListener('change', systemThemeListener);
  } else if (theme === 'dark') {
    // Apply dark theme using Dialtone
    setTheme(DpDark);
  } else {
    // Apply light theme using Dialtone
    setTheme(DpLight);
  }
};

/**
 * Apply color theme
 */
export const applyColorTheme = (themeName: ColorThemeName): void => {
  const htmlElement = document.querySelector('html');

  if (!htmlElement) {
    return;
  }

  // Remove any existing color theme attribute
  htmlElement.removeAttribute('data-color-theme');

  // Store the theme preference
  localStorage.setItem('colorTheme', themeName);

  if (themeName === DEFAULT_COLOR_THEME) {
    // For default theme, don't set any attribute
    // Let Dialtone's built-in theme take over completely
    return;
  }

  // For non-default themes, set the attribute and let CSS handle the styling
  htmlElement.setAttribute('data-color-theme', themeName);
};

/**
 * Apply density setting
 */
export const applyDensity = (densityName: DensityName): void => {
  const htmlElement = document.querySelector('html');

  if (!htmlElement) {
    return;
  }

  // Remove any existing density attribute
  htmlElement.removeAttribute('data-ui-density');

  // Set the new density attribute (only if not medium, which is the default)
  if (densityName !== 'medium') {
    htmlElement.setAttribute('data-ui-density', densityName);
  }

  // Store the preference
  localStorage.setItem('density', densityName);
};

/**
 * Apply contrast mode
 */
export const applyContrastMode = async (mode: ContrastMode): Promise<void> => {
  const html = document.documentElement;

  // Store the preference
  localStorage.setItem('contrastMode', mode);

  if (mode === 'high') {
    // Load contrast styles if not already loaded
    await loadContrastStyles();
    html.setAttribute('data-contrast-mode', 'high');
  } else {
    html.removeAttribute('data-contrast-mode');
  }
};

// ==================== GETTER FUNCTIONS ====================

/**
 * Get current theme
 */
export const getCurrentTheme = (): ThemeType => currentTheme;

/**
 * Get current color theme
 */
export const getCurrentColorTheme = (): ColorThemeName => {
  const saved = localStorage.getItem('colorTheme');
  return saved && isValidColorTheme(saved) ? saved : DEFAULT_COLOR_THEME;
};

/**
 * Get current density
 */
export const getCurrentDensity = (): DensityName => {
  const saved = localStorage.getItem('density');
  return saved && isValidDensity(saved) ? saved : DEFAULT_DENSITY;
};

/**
 * Get current contrast mode
 */
export const getCurrentContrastMode = (): ContrastMode => {
  const saved = localStorage.getItem('contrastMode');
  return saved && isValidContrast(saved) ? saved : DEFAULT_CONTRAST;
};

/**
 * Get available color themes
 */
export const getAvailableColorThemes = (): ColorTheme[] => COLOR_THEMES;

/**
 * Get available density options
 */
export const getAvailableDensities = (): DensityOption[] => DENSITY_OPTIONS;

// ==================== INITIALIZATION ====================

/**
 * Load theme from localStorage and apply it (legacy compatibility)
 */
export const loadTheme = (): void => {
  const storedTheme = localStorage.getItem('theme');
  const theme = storedTheme && isValidTheme(storedTheme) ? storedTheme : DEFAULT_THEME;
  applyTheme(theme);
};

/**
 * Initialize theme system - load all preferences from localStorage
 */
export const initializeThemes = async (): Promise<void> => {
  // Load base theme
  const storedTheme = localStorage.getItem('theme');
  const theme = storedTheme && isValidTheme(storedTheme) ? storedTheme : DEFAULT_THEME;
  applyTheme(theme);

  // Load color theme
  const colorTheme = getCurrentColorTheme();
  applyColorTheme(colorTheme);

  // Load density
  const density = getCurrentDensity();
  applyDensity(density);

  // Load contrast mode (async to potentially load styles)
  const contrast = getCurrentContrastMode();
  await applyContrastMode(contrast);
};

// ==================== LEGACY COMPATIBILITY ====================
// Keep these exports for backward compatibility during migration

export { applyTheme as applyLegacyTheme };
export type { ThemeType as LegacyThemeType };
